import { forceLogout } from "../utils/auth.js";
import { recordEvent } from "./socketDebug.js";

// Liveness supervision for the Phoenix socket.
//
// phoenix.js already detects a dead connection through its heartbeat and retries
// with a backoff, but two situations defeat it and leave the panel showing stale
// loads/sensors/actuators until the operator reloads the page:
//
//   1. Background tabs. Chrome throttles timers in hidden tabs to roughly one
//      tick per minute and freezes the page entirely after a few minutes, so the
//      heartbeat interval both misfires (closing a healthy socket as a "heartbeat
//      timeout") and stops running at all. The retry timer is throttled the same
//      way, so nothing brings the socket back.
//   2. A zombie connection whose readyState is still OPEN — half-open TCP behind
//      NAT, for instance — during a window where the heartbeat timer was killed.
//
// This module closes both holes: it forces a reconnect when the tab comes back to
// the foreground after long enough to have missed heartbeats, and it keeps a
// watchdog that reconnects whenever the socket is closed or has gone quiet while
// visible.
//
// It also handles the one failure the backoff can never recover from on its own:
// `UserSocket.connect/2` refuses the handshake when the token is no longer valid,
// so an expired session turns into an endless retry loop against a server that
// will keep saying no.

// Values are kept in sync with the socket options in apolloClient.jsx.
const HEARTBEAT_INTERVAL = 15000;
const WATCHDOG_INTERVAL = 5000;
// A single trigger (tab focus, network back, a watchdog tick) can fire a forced
// reconnect two or three times within milliseconds, and each reconnect makes
// @absinthe/socket re-push every subscription. On the server those re-pushes pile
// up as duplicate registrations (deterministic doc ids, `:duplicate` registry),
// so a reconnect storm inflates the subscription registry into the thousands and
// spikes CPU. One forced reconnect per window is always enough — phoenix.js owns
// the retry backoff after that.
const FORCED_RECONNECT_COOLDOWN = 3000;
// Three missed heartbeats. Only used as a backstop for when the heartbeat timer
// itself is gone, so it can afford to be conservative.
const STALE_THRESHOLD = HEARTBEAT_INTERVAL * 3;

// A refused handshake fails fast, so with the first backoff steps this triggers a
// few seconds in. Low enough to react quickly, high enough that a device reboot
// (which drops the connection once) never reaches it.
const FAILURES_BEFORE_SESSION_CHECK = 3;
const VALIDATE_PATH = "/api/v1/auth/validate";

// Socket lifecycle is a handful of lines per session when things are healthy, and
// the thing that made the "everything froze" report hard to diagnose was not
// knowing whether the socket had reconnected at all. Cheap to keep, so keep it.
// Also kept in a rolling history so `__socket.history()` can reconstruct what
// happened while nobody was looking at the console.
const log = (event, detail) => {
  recordEvent(event, detail);

  // eslint-disable-next-line no-console
  console.info("[socket]", event, detail === undefined ? "" : detail);
};

const monitorSocket = (socket, { token, apiOrigin } = {}) => {
  let lastActivityAt = Date.now();
  let consecutiveFailures = 0;
  let sessionCheckInFlight = false;
  let closeIsExpected = false;
  let lastForcedReconnectAt = 0;

  const markActivity = () => {
    lastActivityAt = Date.now();
  };

  // Forces a reconnect that the rest of the stack actually notices.
  //
  // This is the subtle part, and getting it wrong kills every subscription until
  // the page is reloaded. `teardown()` and `disconnect()` both replace
  // `conn.onclose` with a no-op before closing, so `onConnClose` never runs —
  // which means `triggerChanError()` never runs either, and the
  // `__absinthe__:control` channel stays in state "joined" on the client while the
  // server, on the new connection, has never seen a `phx_join` for it. phoenix.js
  // only rejoins channels that are errored (`socket.onOpen` ->
  // `isErrored() && rejoin()`), so it never rejoins, and @absinthe/socket never
  // re-joins either because its `channelJoinCreated` flag is set once and never
  // reset. From then on `sendNew` takes the
  // `if (channelJoinCreated) pushRequest(...)` branch and pushes subscriptions
  // onto a topic the server does not have joined: `canPush()` is true (the client
  // thinks it is joined), the message goes out, no reply ever matches, and every
  // subscription dies silently.
  //
  // Closing through the real `onclose` path is what marks the channel errored so
  // it rejoins on reconnect, and the join reply is what makes @absinthe/socket
  // re-push every notifier. This is the same primitive phoenix.js uses for its own
  // heartbeat timeout.
  const reconnectNow = (reason) => {
    const now = Date.now();

    // Collapse bursts: a forced reconnect already in progress does not need to be
    // stacked on top of. Suppressing the extras is what stops the re-push storm.
    if (now - lastForcedReconnectAt < FORCED_RECONNECT_COOLDOWN) {
      log("reconnect suprimido (cooldown)", reason);
      return;
    }

    lastForcedReconnectAt = now;
    log("forcing reconnect", reason);

    if (socket.connectionState() === "open") {
      closeIsExpected = true;
      socket.abnormalClose(`watchdog: ${reason}`);
      return;
    }

    // Already closed: `onConnClose` has run, so the channel is errored and will
    // rejoin. Tearing down only skips the remaining backoff wait, and it does not
    // fire `onClose`, so nothing needs to be marked as expected here.
    socket.teardown(() => socket.connect());
    markActivity();
  };

  socket.onOpen(() => {
    log("open");
    consecutiveFailures = 0;
    markActivity();
  });

  socket.onMessage(markActivity);

  // The browser cannot read the HTTP status of a rejected websocket upgrade, so a
  // refused handshake is indistinguishable from an unreachable device. Asking the
  // API whether the token is still good is what separates the two.
  const checkSession = () => {
    if (sessionCheckInFlight) return;
    sessionCheckInFlight = true;

    fetch(`${apiOrigin}${VALIDATE_PATH}`, {
      headers: { authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((response) => {
        if (response.status === 401) {
          log("session rejected, logging out");
          forceLogout();
          return;
        }

        // Anything else is inconclusive — the device rebooting, a proxy error, a
        // 404 from the dev server. The token is not the problem, so leave the
        // backoff running instead of kicking out a working session.
        consecutiveFailures = 0;
      })
      .catch(() => {
        // Network unreachable: the socket failures are connectivity, not auth.
      })
      .then(() => {
        sessionCheckInFlight = false;
      });
  };

  // Only `onClose` is counted: a failed attempt fires `error` and then `close`,
  // while a plain drop fires only `close`, so this is exactly one per attempt.
  socket.onClose(() => {
    if (closeIsExpected) {
      closeIsExpected = false;
      log("closed by watchdog, reconnecting");
      return;
    }

    consecutiveFailures += 1;
    log("closed", `consecutive failures: ${consecutiveFailures}`);

    if (consecutiveFailures >= FAILURES_BEFORE_SESSION_CHECK) {
      checkSession();
    }
  });

  const check = () => {
    // Timers are throttled while hidden, so both "no traffic" and "time since
    // last activity" are meaningless here. Recovery happens on visibilitychange.
    if (document.hidden) return;

    const state = socket.connectionState();

    // A connect attempt is already in flight, or a close is being processed and
    // phoenix.js will schedule the retry itself.
    if (state === "connecting" || state === "closing") return;

    if (state === "closed") {
      reconnectNow("socket closed");
      return;
    }

    if (Date.now() - lastActivityAt > STALE_THRESHOLD) {
      reconnectNow("no traffic for 3 heartbeats");
    }
  };

  const watchdog = setInterval(check, WATCHDOG_INTERVAL);

  const onVisibilityChange = () => {
    if (document.hidden) return;

    // Long enough hidden that heartbeats were throttled or the page was frozen:
    // the socket cannot be trusted even if it still reports itself as open, and
    // waiting for the watchdog would show stale numbers for up to STALE_THRESHOLD.
    // Reconnecting is cheap on the plant LAN and triggers the onOpen resync.
    if (Date.now() - lastActivityAt > HEARTBEAT_INTERVAL) {
      reconnectNow("tab visible again after being hidden");
      return;
    }

    check();
  };

  // Coming back from a dropped network: reconnect immediately instead of waiting
  // out whatever backoff step the socket had reached.
  const onOnline = () => {
    if (socket.connectionState() !== "open") {
      reconnectNow("network back online");
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("online", onOnline);

  return {
    // A socket is created per session, so in practice the watchdog runs once and
    // lives as long as the page. Exposed so a caller that creates a replacement
    // socket can drop the previous one instead of leaving it ticking against a
    // dead connection.
    stop: () => {
      clearInterval(watchdog);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
    },
    reconnect: reconnectNow,
    stats: () => ({ lastActivityAt, consecutiveFailures }),
  };
};

export default monitorSocket;
